const ACCESS_TOKEN_KEY = 'boss_token';
const REFRESH_TOKEN_KEY = 'boss_refresh_token';

let refreshInFlight: Promise<string | null> | null = null;

function readToken(key: string): string {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function storeToken(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // A later 401 will surface the sign-in path if storage is unavailable.
  }
}

function clearExpiredSession(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    // Continue to the visible sign-in route even if storage is unavailable.
  }

  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('boss-auth-expired'));
  if (!window.location.hash.startsWith('#/login')) {
    window.location.hash = '#/login?reason=session-expired';
  }
}

function withAccessToken(init: RequestInit, accessToken: string): RequestInit {
  const headers = new Headers(init.headers ?? {});
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  else headers.delete('Authorization');
  return { ...init, headers };
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = readToken(REFRESH_TOKEN_KEY);
  if (!refreshToken) return null;

  const response = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!response.ok) return null;

  const data = await response.json().catch(() => null) as {
    accessToken?: unknown;
    refreshToken?: unknown;
  } | null;
  if (typeof data?.accessToken !== 'string' || typeof data?.refreshToken !== 'string') return null;

  storeToken(ACCESS_TOKEN_KEY, data.accessToken);
  storeToken(REFRESH_TOKEN_KEY, data.refreshToken);
  return data.accessToken;
}

async function getFreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Authenticated fetch with one safe refresh-and-retry.
 *
 * BOSS access tokens last 24 hours and refresh tokens last seven days. The
 * browser already stores both. This helper finally uses the refresh token,
 * retries the original request once, and sends the user to a truthful sign-in
 * screen when the entire session is no longer recoverable.
 */
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const first = await fetch(input, withAccessToken(init, readToken(ACCESS_TOKEN_KEY)));
  if (first.status !== 401) return first;

  const freshAccessToken = await getFreshAccessToken().catch(() => null);
  if (!freshAccessToken) {
    clearExpiredSession();
    return first;
  }

  const retried = await fetch(input, withAccessToken(init, freshAccessToken));
  if (retried.status === 401) clearExpiredSession();
  return retried;
}
